/**
 * EnvBadge — coloured SAP environment pill (DEV / QA / UAT / PROD).
 *
 * Used everywhere SAP Systems are displayed so users never confuse a DEV box
 * for PROD. Colour convention: DEV gray · QA orange · UAT purple · PROD green.
 */
import { envCode } from "../lib/smartewmLabels";

const ENV_STYLES = {
  DEV: "bg-gray-100 text-gray-600 border-gray-200",
  QA: "bg-orange-100 text-orange-700 border-orange-200",
  QAS: "bg-orange-100 text-orange-700 border-orange-200",
  UAT: "bg-purple-100 text-purple-700 border-purple-200",
  PROD: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PRD: "bg-emerald-100 text-emerald-700 border-emerald-200",
  SBX: "bg-sky-100 text-sky-700 border-sky-200",
  TST: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function EnvBadge({ env, className = "" }) {
  const code = envCode(env);
  if (!code) return null;
  const style = ENV_STYLES[code] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${style} ${className}`}
      data-testid={`env-badge-${code.toLowerCase()}`}
    >
      {code}
    </span>
  );
}

/**
 * ClientLogo — small logo thumbnail with a default fallback icon.
 * `logo` is a data URL (or empty). `size` is the pixel box.
 */
export function ClientLogo({ logo, name, size = 20, className = "" }) {
  if (logo) {
    return (
      <img loading="lazy"
        src={logo}
        alt={name || "client logo"}
        className={`object-contain rounded-sm bg-white border border-[#E5E7EB] ${className}`}
        style={{ width: size, height: size }}
        data-testid="client-logo-img"
      />
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center rounded-sm bg-[#E0F2FE] text-[#025979] font-bold ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(9, size * 0.45) }}
      data-testid="client-logo-fallback"
    >
      {(name || "?").trim().charAt(0).toUpperCase()}
    </span>
  );
}
